import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, Package, Ruler, FileText, ImageIcon, MapPin, Clock, ClipboardList, CheckCircle2, Download, Send, Upload, X, ChevronDown, ChevronRight, Calendar as CalIcon, CalendarDays, NotebookPen, Check, PoundSterling } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getQuote, deleteQuote, getMediaUrl, getInvoicePdfUrl, getQuotePdfUrl, getMaterialsPdfUrl, sendInvoiceEmail, sendQuoteEmail, uploadMedia, deleteMedia, updateItem, updateQuote, CLIENT_COLORS, getPricingSettings } from "@/lib/api";
import api from "@/lib/api";
import { computeTotals, formatCurrency, getPricing, FIXED_VAT_RATE } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";
import { AiJobSummary, AiEmailDrafter } from "@/components/custom/AiFeatures";
import PaymentReceiptModal from "@/components/custom/PaymentReceiptModal";
import QuoteFamilySelector from "@/components/custom/QuoteFamilySelector";

const HOURS_PER_DAY = 8;
function fmtDuration(h) {
  if (!h || h === 0) return "0 hrs";
  if (h >= HOURS_PER_DAY) { const d = h / HOURS_PER_DAY; return `${d % 1 === 0 ? d : d.toFixed(1)} day${d !== 1 ? "s" : ""}`; }
  return `${h} hrs`;
}

export default function QuoteView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete] = useState(false); // legacy state kept only to avoid a wider refactor; the new selector handles delete itself
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [showSendInvoice, setShowSendInvoice] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState({ to_email: "", cc_email: "", subject: "", message: "" });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [showSendReminder, setShowSendReminder] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showSendQuote, setShowSendQuote] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState({ to_email: "", cc_email: "", subject: "", message: "" });
  const [sendingQuote, setSendingQuote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchParams] = useSearchParams();
  const [showRebook, setShowRebook] = useState(false);
  const [rebookSelected, setRebookSelected] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showPhotoSelect, setShowPhotoSelect] = useState(null); // "quote" | "invoice" | null
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState("create");   // "create" or "edit"
  const [pricingRules, setPricingRules] = useState({});
  const [showRefund, setShowRefund] = useState(false);
  const [editPay, setEditPay] = useState({ payment_date: "", amount_paid: 0, payment_method: "Bank Transfer", notes: "" });
  const [refundData, setRefundData] = useState({ refund_amount: "", refund_date: "", reason: "", method: "Bank Transfer", notes: "" });
  const [savingPayment, setSavingPayment] = useState(false);
  const [issuingRefund, setIssuingRefund] = useState(false);

  const toggleSection = (key) => setCollapsedSections(p => ({ ...p, [key]: !p[key] }));

  useEffect(() => {
    getPricingSettings().then(d => setPricingRules(d?.rules || d || {})).catch(() => {});
  }, []);

  const loadQuote = useCallback(async () => {
    try {
      const data = await getQuote(id);
      // Active Quote era (31/05/2026) — we no longer auto-redirect from a "parent with an
      // accepted primary" because the office user has explicit Active Quote control via the
      // QuoteFamilySelector. Instead, if the user is viewing a Superseded quote, the
      // selector shows the "Superseded" pill on this card and the banner below the header
      // tells them Send / Invoice are disabled. They can switch Active explicitly.
      setQuote(data);
      if (searchParams.get("invoice") === "true" && data) {
        setInvoiceEmail({ to_email: data.correspondence_email || "", cc_email: "", subject: `Invoice ${data.quote_number || ""} - ${data.site_address || ""}`, message: `Hi ${[data.client_first_name, data.client_last_name].filter(Boolean).join(" ") || data.client_type || ""},\n\nPlease see your invoice attached. Photos are included at the bottom of the document.\n\nIf you have any questions please feel free to reach out.\n\nMany Thanks` });
        setShowSendInvoice(true);
      }
      if (searchParams.get("rebook") === "true" && data) {
        const ac = data.action_completion || {};
        const status = ac.status || {};
        const mainActs = (data.actions || []).filter(a => typeof a === "object" && !a.is_sub);
        const incomplete = mainActs.filter((_, idx) => !status[idx + 1]);
        if (incomplete.length > 0) {
          const sel = {};
          incomplete.forEach((_, i) => { const num = mainActs.indexOf(incomplete[i]) + 1; sel[num] = true; });
          setRebookSelected(sel);
          setShowRebook(true);
        }
      }
    } catch { navigate("/"); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { setLoading(true); loadQuote(); }, [loadQuote]);

  useEffect(() => { if (quote && (quote.has_duplicates || quote.parent_quote_id || quote.is_duplicate)) loadDuplicates(); }, [quote?.id]);

  const totals = useMemo(() => { if (!quote) return {}; return computeTotals(quote, quote.items || []); }, [quote]);

  const handleDelete = async () => { try { await deleteQuote(id); toast.success("Quote deleted"); navigate(-1); } catch { toast.error("Failed to delete"); } }; // eslint-disable-line no-unused-vars

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const resp = await api.post(`/quotes/${id}/duplicate`);
      toast.success(`Duplicate created: ${resp.data.duplicate_label}`);
      loadDuplicates();
      setShowDuplicates(true);
    } catch (err) { console.error(err); toast.error("Failed to duplicate"); }
    finally { setDuplicating(false); }
  };

  const loadDuplicates = async () => {
    try { const resp = await api.get(`/quotes/${id}/duplicates`); setDuplicates(resp.data); } catch (err) { console.error(err); }
  };

  const selectForSend = async (dupId) => {
    try { await api.post(`/quotes/${dupId}/select-for-send`); toast.success("Selected for sending"); loadDuplicates(); } catch { toast.error("Failed"); }
  };

  const handleSendInvoice = async () => {
    if (!invoiceEmail.to_email) { toast.error("Please enter an email address"); return; }
    setSendingInvoice(true);
    try { await sendInvoiceEmail(id, invoiceEmail); toast.success("Invoice sent — status updated to Invoiced"); setShowSendInvoice(false); navigate("/"); }
    catch { toast.error("Failed to send invoice"); } finally { setSendingInvoice(false); }
  };

  const handleSendQuote = async () => {
    if (!quoteEmail.to_email) { toast.error("Please enter an email address"); return; }
    setSendingQuote(true);
    try { await sendQuoteEmail(id, quoteEmail); toast.success("Quote sent"); setShowSendQuote(false); navigate("/"); }
    catch { toast.error("Failed to send quote"); } finally { setSendingQuote(false); }
  };

  if (loading || !quote) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;
  }

  const photos = (quote.media || []).filter(m => m.category === "photo" && !m.is_deleted);
  const documents = (quote.media || []).filter(m => m.category === "document");
  const receipts = (quote.media || []).filter(m => m.category === "receipt");
  const actions = quote.actions || [];
  const items = quote.items || [];

  const handleToggleOrdered = async (item) => {
    const newVal = !item.ordered;
    setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, ordered: newVal } : it) }));
    try {
      await updateItem(id, item.id, { ordered: newVal });
      // Auto-update materials_status
      const updatedItems = quote.items.map(it => it.id === item.id ? { ...it, ordered: newVal } : it);
      const allOrdered = updatedItems.every(it => it.ordered);
      if (allOrdered && updatedItems.length > 0) {
        setQuote(prev => ({ ...prev, materials_status: "Ordered" }));
        await updateQuote(id, { materials_status: "Ordered" });
      } else if (!allOrdered && quote.materials_status === "Ordered") {
        setQuote(prev => ({ ...prev, materials_status: "Not Started" }));
        await updateQuote(id, { materials_status: "Not Started" });
      }
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-6 pb-4"}>
        <div className={`flex ${isMobile ? "flex-col gap-3" : "items-center justify-between"}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn" className="btn-secondary h-9">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className={isMobile ? "text-base font-semibold" : "text-xl font-semibold"} style={{ color: "var(--text-primary)" }}>
              View Job Pack
            </h1>
            {quote.quote_number && (
              <span className="text-sm font-mono font-semibold" style={{ color: "var(--primary)" }} data-testid="quote-number">{quote.quote_number}</span>
            )}
            <span className="badge-pill badge-blue text-xs" data-testid="view-client-badge">{quote.client_type}</span>
            {quote.works_type === "Works Order" && (
              (quote.total_inc_vat || 0) >= 500 ? (
                <span className="badge-pill badge-danger text-xs">Requires Quote</span>
              ) : (
                <span className="badge-pill text-xs" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>WO</span>
              )
            )}
            {quote.materials_status === "Ordered" && (
              <span className="badge-pill badge-success text-xs">Materials Ordered</span>
            )}
          </div>
          <div className={`flex gap-2 ${isMobile ? "flex-wrap" : "flex-wrap"}`}>
            <AiJobSummary jobId={id} onResult={(summary) => { toast.success("Summary copied to clipboard"); navigator.clipboard.writeText(summary); }} />
            {quote.quote_started && (
              <Button variant="outline" size="sm" onClick={() => {
                // If duplicates exist and THIS quote isn't the active one, confirm before sending (per spec)
                const thisDup = duplicates.find(d => d.id === id);
                const activeDup = duplicates.find(d => d.selected_for_send);
                if (duplicates.length > 1 && thisDup && !thisDup.selected_for_send) {
                  const thisLabel = thisDup.duplicate_label || (thisDup.is_duplicate ? "this option" : "Original Quote");
                  const activeLabel = activeDup ? (activeDup.duplicate_label || (activeDup.is_duplicate ? activeDup.duplicate_label : "Original Quote")) : "the original";
                  const ok = window.confirm(`You are about to send ${thisLabel}. This will set ${thisLabel} as the active quote for this job and update the job status. ${activeLabel} will become inactive. Do you want to continue?`);
                  if (!ok) return;
                }
                const allPhotos = (quote.media || []).filter(m => m.category === "photo" && !m.is_deleted);
                setSelectedPhotoIds(new Set(allPhotos.map(p => p.id)));
                if (allPhotos.length > 0) {
                  setShowPhotoSelect("quote");
                } else {
                  setShowSendQuote(true);
                }
              }} className="rounded-lg gap-1" style={{ borderColor: "var(--primary)", color: "var(--primary)" }} data-testid="send-quote-btn">
                <Send className="w-4 h-4" /> Send Quote
              </Button>
            )}
            {quote.quote_started && (
              quote.status === "Invoiced" ? (
                <>
                  <Button size="sm" className="rounded-lg gap-1 bg-emerald-600 hover:bg-emerald-600 text-white cursor-default" data-testid="invoice-sent-btn">
                    <CheckCircle2 className="w-4 h-4" /> Invoice Sent
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}/profit`)} className="rounded-lg gap-1" style={{ borderColor: "#E05C2A", color: "#E05C2A" }} data-testid="calculate-profit-btn">
                    <PoundSterling className="w-4 h-4" /> Calculate Profit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    const allPhotos = (quote.media || []).filter(m => m.category === "photo" && !m.is_deleted);
                    setSelectedPhotoIds(new Set(allPhotos.map(p => p.id)));
                    if (allPhotos.length > 0) {
                      setShowPhotoSelect("invoice");
                    } else {
                      setShowSendInvoice(true);
                    }
                  }} className="rounded-lg gap-1" style={{ borderColor: "var(--primary)", color: "var(--primary)" }} data-testid="resend-invoice-btn">
                    <Send className="w-4 h-4" /> Resend Invoice
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => {
                  const allPhotos = (quote.media || []).filter(m => m.category === "photo" && !m.is_deleted);
                  setSelectedPhotoIds(new Set(allPhotos.map(p => p.id)));
                  if (allPhotos.length > 0) {
                    setShowPhotoSelect("invoice");
                  } else {
                    setShowSendInvoice(true);
                  }
                }} disabled={quote.selected_for_send === false} title={quote.selected_for_send === false ? "Disabled — this is an Inactive Quote." : undefined} className="rounded-lg gap-1" style={{ borderColor: "var(--success)", color: "#15803D", opacity: quote.selected_for_send === false ? 0.5 : 1 }} data-testid="send-invoice-btn">
                  <Send className="w-4 h-4" /> Send Invoice
                </Button>
              )
            )}
            <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}/edit`, { replace: true })} className="rounded-lg gap-1" style={{ border: "1px solid var(--border)" }} data-testid="edit-quote-btn">
              <Edit className="w-4 h-4" /> Edit
            </Button>
            {quote.status === "Awaiting Quote" && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/calendar?book=${id}`)} className="rounded-lg gap-1" style={{ border: "1px solid #3B82F6", color: "#3B82F6" }}>
                <CalendarDays className="w-4 h-4" /> Book Quote Visit
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleDuplicate} disabled={duplicating} className="rounded-lg gap-1" style={{ border: "1px solid var(--border)" }}>
              {duplicating ? "Duplicating..." : "Duplicate"}
            </Button>
            {/* The old "Multiple Quotes" toggle + collapsible panel were replaced by the always-visible
                QuoteFamilySelector at the top of the body. The header-level Delete button is also
                gone — Delete now lives in the per-card overflow menu with safety-branch logic. */}
          </div>
        </div>
      </div>

      {/* Active-Quote selector — always visible, shows all quotes in the family with Active/Superseded
          styling, overflow menus, and the modal-driven safety flows. Stage 2 of the Active Quote spec. */}
      <div className={`${isMobile ? "px-2" : "px-6"} pb-2 pt-1`}>
        <QuoteFamilySelector
          currentQuoteId={id}
          onFamilyChange={() => { loadQuote(); }}
        />
      </div>

      {/* Inactive-quote banner — surfaces only when the currently-viewed quote is NOT the family's
          Active Quote. Tells the user that Send Quote and Send Invoice are not available on
          this view (per spec section 7) and offers a one-click jump to the Active Quote. */}
      {quote && quote.selected_for_send === false && (
        <div
          className={`${isMobile ? "mx-2" : "mx-6"} mb-3 px-4 py-3 rounded-lg flex items-start gap-3`}
          style={{ background: "#FFF7ED", border: "1px solid #FDBA74" }}
          data-testid="superseded-banner"
        >
          <div className="text-xl leading-none mt-0.5" style={{ color: "#C2410C" }}>!</div>
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: "#9A3412" }}>This is an Inactive Quote</div>
            <div className="text-xs mt-0.5" style={{ color: "#9A3412" }}>
              Send Quote and Send Invoice actions are disabled. To send to the client or
              invoice this job, switch to the Active Quote — pick it from the selector above,
              or click the button to jump straight there.
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-7xl mx-auto ${isMobile ? "px-4 py-3" : "px-8 py-4"}`}>
        <ProfitPromptBanner quote={quote} navigate={navigate} id={id} />
        <div className={`grid grid-cols-1 ${isMobile ? "" : "lg:grid-cols-12"} gap-6`}>
          {/* Main content */}
          <div className={isMobile ? "" : "lg:col-span-8"}>

            {/* Quote Info Bar */}
            {quote.quote_number && (
              <div className="ui-surface p-5 mb-6 flex items-center justify-between" data-testid="quote-info-bar">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>Quote Number</p>
                    <p className="text-lg font-mono font-bold mt-0.5" style={{ color: "var(--primary)" }}>{quote.quote_number}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>Quote Date</p>
                    <p className="text-lg font-mono font-bold mt-0.5" style={{ color: "var(--text-primary)" }}>{quote.quote_date}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>Status</p>
                    <p className="text-sm font-semibold mt-1">
                      <span className="badge-pill badge-blue">{quote.status || "Not Started"}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Site Details */}
            <section className="ui-surface p-6 mb-6" data-testid="view-site-details">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>
                <MapPin className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Site Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
                <div className="col-span-2 md:col-span-3">
                  <InfoCell label="Address" value={quote.site_address} />
                </div>
                <InfoCell label="Client Type" value={quote.client_type} />
                <InfoCell label="Client Name" value={[quote.client_first_name, quote.client_last_name].filter(Boolean).join(" ")} />
                {quote.correspondence_email && <InfoCell label="Email" value={quote.correspondence_email} />}
                {quote.correspondence_phone && <InfoCell label="Phone" value={quote.correspondence_phone} />}
                {quote.tenant_phone && <InfoCell label="Tenant Phone" value={quote.tenant_phone} />}
                <InfoCell label="Created" value={new Date(quote.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })} />
                <InfoCell label="Updated" value={new Date(quote.updated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })} />
              </div>
            </section>

            {/* Job Details */}
            {quote.job_details && (
              <section className="ui-surface p-6 mb-6" data-testid="view-job-details-section">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                  <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Job Description
                </h2>
                <MarkdownText text={quote.job_details} />
              </section>
            )}

            {/* Quote Visit Notes */}
            {quote.quote_visit_notes && (
              <section className="mb-6 p-5 rounded-xl" style={{ background: "#FFFBEB", border: "2px solid #F59E0B" }}>
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-3" style={{ color: "#92400E" }}>
                  Quote Visit Notes
                </h2>
                <p className="text-sm whitespace-pre-wrap" style={{ color: "#78350F" }}>{quote.quote_visit_notes}</p>
              </section>
            )}

            {/* Internal Notes — moved to its own always-visible card below Timeline */}

            {/* Recall Info */}
            {(quote.status === "Recall" || quote.status === "Recall Booked" || quote.recall_reason || quote.pre_recall_status) && (
              <section className="mb-6 p-5 rounded-xl" style={{ background: "#FFF7ED", border: "2px solid #F97316" }}>
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-3" style={{ color: "#9A3412" }}>
                  Recall Information
                </h2>
                {quote.recall_reason && <p className="text-sm mb-2"><span className="font-semibold" style={{ color: "#9A3412" }}>Reason:</span> <span style={{ color: "#78350F" }}>{quote.recall_reason}</span></p>}
                {quote.pre_recall_status && <p className="text-sm mb-2"><span className="font-semibold" style={{ color: "#9A3412" }}>Previous Status:</span> <span style={{ color: "#78350F" }}>{quote.pre_recall_status}</span></p>}
                {quote.recall_actions && quote.recall_actions.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ color: "#9A3412" }}>Recalled Actions:</p>
                    {quote.recall_actions.map(num => {
                      const mainActs = actions.filter(a => typeof a === "object" && !a.is_sub);
                      const act = mainActs[num - 1];
                      return act ? <p key={num} className="text-sm ml-3" style={{ color: "#78350F" }}>• {num}. {act.title || act.description || "Action"}</p> : null;
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Services / Actions */}
            {actions.length > 0 && (
              <section className="mb-6">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Services ({actions.filter(a => typeof a === "object" && !a.is_sub).length})</h2>
                <div className="space-y-3">
                  {(() => {
                    let mainNum = 0, subNum = 0;
                    const groups = [];
                    actions.forEach((action, i) => {
                      const act = typeof action === "string" ? { description: action, hours: 0, is_sub: false } : action;
                      if (!act.is_sub) {
                        mainNum++; subNum = 0;
                        const price = act.price != null && act.price > 0 ? act.price : 0;
                        const subs = [];
                        let j = i + 1;
                        while (j < actions.length && actions[j]?.is_sub) { subNum++; subs.push({ ...actions[j], label: `${mainNum}.${subNum}` }); j++; }
                        const actionItems = items.filter(it => it.action_index === i);
                        groups.push({ num: mainNum, idx: i, act, price, subs, items: actionItems });
                      }
                    });
                    return groups.map(g => {
                      const collapsed = collapsedSections[`action-${g.idx}`];
                      return (
                        <div key={g.idx} className="ui-surface overflow-hidden" data-testid={`view-service-${g.num}`}>
                          <div className="flex items-center justify-between px-5 py-4 cursor-pointer select-none hover:bg-slate-50/50 transition-colors" style={{ borderBottom: collapsed ? "none" : "1px solid var(--border)" }} onClick={() => toggleSection(`action-${g.idx}`)}>
                            <div className="flex items-center gap-3">
                              {collapsed ? <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
                              <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{g.num}. {g.act.title || g.act.description || "Service"}</span>
                            </div>
                            <span className="text-sm font-mono font-bold" style={{ color: "var(--text-primary)" }}>{formatCurrency(g.price)}</span>
                          </div>
                          {!collapsed && (
                            <div className="px-5 py-4">
                              {g.act.description && <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{g.act.description}</p>}
                              <div className="flex items-center gap-6 mb-3">
                                {g.act.hours > 0 && (
                                  <div>
                                    <span className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>Duration</span>
                                    <p className="text-sm font-mono font-semibold mt-0.5">{fmtDuration(g.act.hours)}</p>
                                  </div>
                                )}
                                <div>
                                  <span className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>Rate</span>
                                  <p className="text-sm font-mono font-semibold mt-0.5">{formatCurrency(g.price)}</p>
                                </div>
                              </div>
                              {/* Sub-actions */}
                              {g.subs.length > 0 && (
                                <div className="space-y-1.5 mb-3">
                                  {g.subs.map((sub, si) => (
                                    <div key={si} className="flex items-start gap-3 py-2 pl-4" style={{ borderLeft: "2px solid var(--border)" }}>
                                      <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{sub.label}</span>
                                      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{sub.description || "—"}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Materials linked to this action */}
                              {g.items.length > 0 && (
                                <div className="rounded-lg overflow-hidden mt-2" style={{ border: "1px solid var(--border)" }}>
                                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ background: "var(--bg)", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <div className="col-span-1"></div>
                                    <div className="col-span-2">Supplier</div>
                                    <div className="col-span-2">Item</div>
                                    <div className="col-span-1">SKU</div>
                                    <div className="col-span-2">Location</div>
                                    <div className="col-span-1 text-right">Price</div>
                                    <div className="col-span-1 text-right">Qty</div>
                                    <div className="col-span-2 text-right">Total</div>
                                  </div>
                                  {g.items.map((item, ii) => (
                                    <div key={item.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-start" style={{ borderBottom: ii < g.items.length - 1 ? "1px solid var(--border)" : "none" }}>
                                      <div className="col-span-1 pt-1">
                                        <button onClick={() => handleToggleOrdered(item)} className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors" style={{ background: item.ordered ? "var(--success)" : "transparent", border: `2px solid ${item.ordered ? "var(--success)" : "var(--border)"}` }}>
                                          {item.ordered && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                        </button>
                                      </div>
                                      <div className="col-span-2"><span className="badge-pill badge-blue text-[10px]">{item.supplier}</span></div>
                                      <div className="col-span-2 font-medium text-xs">{item.item_name || "—"}</div>
                                      <div className="col-span-1 font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>{item.sku || "—"}</div>
                                      <div className="col-span-2 text-xs" style={{ color: "var(--text-muted)" }}>{item.location || "—"}</div>
                                      <div className="col-span-1 text-right font-mono text-xs">{formatCurrency(item.unit_price)}</div>
                                      <div className="col-span-1 text-right font-mono text-xs">{item.qty}</div>
                                      <div className="col-span-2 text-right font-mono font-bold text-xs">{formatCurrency((item.unit_price || 0) * (item.qty || 0))}</div>
                                      {/* Collection details row */}
                                      {(item.location || "").toLowerCase().includes("click and collect") && (
                                        <div className="col-span-12 flex gap-2 mt-1 ml-7">
                                          <input defaultValue={item.order_number || ""} onBlur={(e) => { if (e.target.value !== (item.order_number || "")) { updateItem(id, item.id, { order_number: e.target.value }); setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, order_number: e.target.value } : it) })); } }} placeholder="Order / collection code" className="h-7 rounded text-xs font-mono px-2 w-40" style={{ border: "1px solid var(--border)" }} />
                                          <input defaultValue={item.branch_location || ""} onBlur={(e) => { if (e.target.value !== (item.branch_location || "")) { updateItem(id, item.id, { branch_location: e.target.value }); setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, branch_location: e.target.value } : it) })); } }} placeholder="Branch location" className="h-7 rounded text-xs px-2 w-40" style={{ border: "1px solid var(--border)" }} />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </section>
            )}

            {/* General Materials (unlinked) */}
            {(() => {
              const unlinked = items.filter(it => it.action_index == null || it.action_index < 0);
              if (unlinked.length === 0 && items.length === 0 && actions.length > 0) return null;
              if (unlinked.length === 0 && actions.length > 0) return null;
              return (
                <section className="ui-surface overflow-hidden mb-6" data-testid="view-items-section">
                  <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
                    <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>
                      <Package className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Materials ({items.length})
                    </h2>
                    {items.length > 0 && (
                      <a href={getMaterialsPdfUrl(id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors" style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="download-materials-pdf">
                        <Download className="w-3.5 h-3.5" /> Materials Sheet
                      </a>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No materials</div>
                  ) : unlinked.length > 0 ? (
                    <div className="rounded-lg overflow-hidden m-4" style={{ border: "1px solid var(--border)" }}>
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ background: "var(--bg)", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                        <div className="col-span-1"></div>
                        <div className="col-span-2">Supplier</div>
                        <div className="col-span-2">Item</div>
                        <div className="col-span-1">SKU</div>
                        <div className="col-span-2">Location</div>
                        <div className="col-span-1 text-right">Price</div>
                        <div className="col-span-1 text-right">Qty</div>
                        <div className="col-span-2 text-right">Total</div>
                      </div>
                      {unlinked.map((item, i) => (
                        <div key={item.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-start" style={{ borderBottom: i < unlinked.length - 1 ? "1px solid var(--border)" : "none" }} data-testid={`view-item-row-${i}`}>
                          <div className="col-span-1 pt-1">
                            <button onClick={() => handleToggleOrdered(item)} className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors" style={{ background: item.ordered ? "var(--success)" : "transparent", border: `2px solid ${item.ordered ? "var(--success)" : "var(--border)"}` }}>
                              {item.ordered && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          </div>
                          <div className="col-span-2"><span className="badge-pill badge-blue text-[10px]">{item.supplier}</span></div>
                          <div className="col-span-2 font-medium text-xs">{item.item_name || "—"}</div>
                          <div className="col-span-1 font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>{item.sku || "—"}</div>
                          <div className="col-span-2 text-xs" style={{ color: "var(--text-muted)" }}>{item.location || "—"}</div>
                          <div className="col-span-1 text-right font-mono text-xs">{formatCurrency(item.unit_price)}</div>
                          <div className="col-span-1 text-right font-mono text-xs">{item.qty}</div>
                          <div className="col-span-2 text-right font-mono font-bold text-xs">{formatCurrency((item.unit_price || 0) * (item.qty || 0))}</div>
                          {(item.location || "").toLowerCase().includes("click and collect") && (
                            <div className="col-span-12 flex gap-2 mt-1 ml-7">
                              <input defaultValue={item.order_number || ""} onBlur={(e) => { if (e.target.value !== (item.order_number || "")) { updateItem(id, item.id, { order_number: e.target.value }); setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, order_number: e.target.value } : it) })); } }} placeholder="Order / collection code" className="h-7 rounded text-xs font-mono px-2 w-40" style={{ border: "1px solid var(--border)" }} />
                              <input defaultValue={item.branch_location || ""} onBlur={(e) => { if (e.target.value !== (item.branch_location || "")) { updateItem(id, item.id, { branch_location: e.target.value }); setQuote(prev => ({ ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, branch_location: e.target.value } : it) })); } }} placeholder="Branch location" className="h-7 rounded text-xs px-2 w-40" style={{ border: "1px solid var(--border)" }} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })()}

            {/* Measurements */}
            {(quote.measurements || []).length > 0 && (
              <section className="ui-surface p-6 mb-6" data-testid="view-measurements-section">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                  <Ruler className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Measurements
                </h2>
                <div className="space-y-2">
                  {quote.measurements.map((m, i) => (
                    <div key={i} className="flex items-center gap-4 py-2" style={{ borderBottom: i < quote.measurements.length - 1 ? "1px solid var(--border)" : "none" }}>
                      <span className="font-medium text-sm flex-1">{m.label || "—"}</span>
                      <span className="font-mono text-sm">{m.value}</span>
                      <span className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>{m.unit}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Parking */}
            {quote.parking_type && (
              <section className="ui-surface p-6 mb-6" data-testid="view-parking">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Parking</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <InfoCell label="Type" value={quote.parking_type} />
                  {quote.parking_hours > 0 && <InfoCell label="Hours" value={quote.parking_hours} />}
                  {quote.parking_rate > 0 && <InfoCell label="Rate (inc VAT)" value={`${formatCurrency(quote.parking_rate)}/hr`} mono />}
                  {quote.parking_hours > 0 && quote.parking_rate > 0 && <InfoCell label="Total" value={formatCurrency(quote.parking_hours * quote.parking_rate)} mono />}
                </div>
              </section>
            )}

            {/* Job Photos — always visible, with own upload */}
            <section className="ui-surface p-6 mb-6" data-testid="view-photos-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <ImageIcon className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Job Photos ({photos.length})
              </h2>
              <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>These photos appear on the quote and invoice PDF.</p>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {photos.map(p => (
                    <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer hover:ring-2 transition-all" style={{ border: "1px solid var(--border)" }} data-testid={`view-photo-${p.id}`}>
                      <img src={getMediaUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" onClick={() => setEnlargedPhoto(p)} />
                      <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm("Delete this photo?")) return; try { await deleteMedia(id, p.id); toast.success("Photo deleted"); loadQuote(); } catch (err) { console.error(err); toast.error("Failed to delete"); } }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.6)" }}>
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className={`flex items-center justify-center gap-2 py-4 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} style={{ border: "2px dashed var(--border)" }} data-testid="view-photo-upload-label">
                <ImageIcon className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>{uploading ? "Uploading..." : "Add Job Photo"}</span>
                <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, 20);
                  if (files.length === 0) return;
                  setUploading(true);
                  for (const f of files) { try { await uploadMedia(id, f, "photo"); } catch (err) { console.error(err); } }
                  toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
                  loadQuote(); setUploading(false); e.target.value = "";
                }} data-testid="view-photo-upload" />
              </label>
            </section>

            {/* Contractor & Booking Details */}
            {quote.booked_team_member_name && (
              <section className="ui-surface p-6 mb-6" data-testid="view-job-sheet-info">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>
                  <ClipboardList className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Booking & Contractor Details
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
                  <InfoCell label="Lead Contractor" value={quote.booked_team_member_name} />
                  <InfoCell label="Start Date" value={quote.start_date || "—"} />
                  {quote.end_date && quote.end_date !== quote.start_date && <InfoCell label="End Date" value={quote.end_date} />}
                  {quote.booked_start_time && quote.booked_end_time && <InfoCell label="Time" value={`${quote.booked_start_time} — ${quote.booked_end_time}`} />}
                  {quote.booking_type && <InfoCell label="Booking Type" value={quote.booking_type === "quote" ? "Quote Appointment" : quote.booking_type === "recall" ? "Recall" : "Job Execution"} />}
                  {quote.booked_duration && <InfoCell label="Duration" value={`${quote.booked_duration}h`} />}
                </div>
                {/* Additional Team Members */}
                {(quote.additional_team_members || []).length > 0 && (
                  <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Additional Team</p>
                    <div className="space-y-1">
                      {quote.additional_team_members.map((am, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="font-medium">{am.name}</span>
                          {am.start_time && am.end_time && <span style={{ color: "var(--text-muted)" }}>{am.start_time} — {am.end_time}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(quote.status === "Job Booked" || quote.booking_type === "job") && (
                  <Button size="sm" onClick={() => navigate(`/jobs/${id}`)} className="mt-4 gap-1" style={{ background: "var(--primary)", color: "#fff" }} data-testid="view-open-jobsheet">
                    Open Contractor Job Sheet
                  </Button>
                )}
              </section>
            )}

            {/* Action Completion Report */}
            {quote.action_completion && (
              <section className="ui-surface p-6 mb-6" data-testid="view-action-completion">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>Action Completion Report</h2>
                  {(() => {
                    const ac = quote.action_completion;
                    const status = ac.status || {};
                    const mainActs = actions.filter(a => typeof a === "object" && !a.is_sub);
                    const incomplete = mainActs.filter((_, idx) => !status[idx + 1]);
                    if (incomplete.length === 0) return null;
                    return (
                      <Button size="sm" onClick={() => { const sel = {}; incomplete.forEach((_, i) => { const num = mainActs.indexOf(incomplete[i]) + 1; sel[num] = true; }); setRebookSelected(sel); setShowRebook(true); }} className="gap-1 text-xs rounded-lg" style={{ background: "var(--warning)", color: "#fff" }} data-testid="rebook-btn">
                        Re-book Incomplete
                      </Button>
                    );
                  })()}
                </div>
                {(() => {
                  const ac = quote.action_completion;
                  const status = ac.status || {};
                  const reasons = ac.reasons || {};
                  const mainActs = actions.filter(a => typeof a === "object" && !a.is_sub);
                  return (
                    <div className="space-y-2">
                      {mainActs.map((a, idx) => {
                        const num = idx + 1;
                        const done = !!status[num];
                        const reason = reasons[num] || "";
                        return (
                          <div key={idx} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: done ? "var(--success-light)" : "var(--danger-light)", border: `1px solid ${done ? "var(--success)" : "var(--danger)"}` }}>
                            <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: done ? "var(--success)" : "var(--danger)" }}>
                              {done ? <CheckCircle2 className="w-3 h-3 text-white" /> : <X className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold">{num}. {a.title || a.description || "Action"}</p>
                              {!done && reason && <p className="text-xs mt-1" style={{ color: "#B91C1C" }}>Reason: {reason}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Work Report */}
            {quote.work_report && (
              <section className="mb-6 p-5 rounded-xl" style={{ background: "#F0FDF4", border: "2px solid #22C55E" }}>
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-3" style={{ color: "#15803D" }}>
                  Contractor Work Report
                </h2>
                <p className="text-sm whitespace-pre-wrap" style={{ color: "#14532D" }}>{quote.work_report}</p>
              </section>
            )}

            {/* Additional Terms */}
            <section className="ui-surface p-6 mb-6" data-testid="view-additional-terms">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Additional Terms
              </h2>
              {quote.additional_terms ? (
                <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{quote.additional_terms}</p>
              ) : (
                <p className="text-sm" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No additional terms added.</p>
              )}
            </section>

            {/* Documentation */}
            {quote.quote_started && (
              <section className="ui-surface p-6 mb-6" data-testid="view-documentation">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Documentation</h2>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 py-2.5 px-4 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                    <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--primary)" }} />
                    <span className="text-sm font-medium flex-1">Quote — {quote.quote_number}</span>
                    <a href={getQuotePdfUrl(id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--primary)" }} data-testid="doc-download-quote">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                  </div>
                  {(quote.status === "Invoiced" || quote.status === "Job Booked") && (
                    <div className="flex items-center gap-3 py-2.5 px-4 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--success)" }} />
                      <span className="text-sm font-medium flex-1">Invoice — {quote.invoice_number || (quote.quote_number || "").replace("Q-", "I-")}</span>
                      <a href={getInvoicePdfUrl(id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--primary)" }} data-testid="doc-download-invoice">
                        <Download className="w-3.5 h-3.5" /> PDF
                      </a>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Material Purchases / Receipts — accounting only, never on PDFs */}
            <section className="ui-surface p-6 mb-6" data-testid="view-material-purchases">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-1" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Material Purchases
              </h2>
              <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>For your records only — these do not appear on any PDF.</p>
              {receipts.length > 0 && (
                <div className="space-y-2 mb-4">
                  {receipts.map(r => (
                    <div key={r.id} className="flex items-center gap-3 py-2.5 px-4 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                      <a href={getMediaUrl(r.storage_path)} target="_blank" rel="noopener noreferrer" className="text-sm font-medium flex-1 truncate" style={{ color: "var(--primary)" }}>{r.original_filename}</a>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{r.size ? `${(r.size / 1024).toFixed(1)} KB` : ""}</span>
                      <button onClick={async () => { try { await deleteMedia(id, r.id); toast.success("Deleted"); loadQuote(); } catch { toast.error("Failed"); } }} style={{ color: "var(--text-muted)" }}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <label className={`flex items-center justify-center gap-2 py-4 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} style={{ border: "2px dashed var(--border)" }}>
                <Upload className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>{uploading ? "Uploading..." : "Upload receipt or invoice"}</span>
                <input type="file" accept="image/*,.pdf,.doc,.docx" multiple className="hidden" onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, 20);
                  if (files.length === 0) return;
                  setUploading(true);
                  for (const f of files) { try { await uploadMedia(id, f, "receipt"); } catch (err) { console.error(err); } }
                  toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
                  loadQuote(); setUploading(false); e.target.value = "";
                }} data-testid="view-receipt-upload" />
              </label>
            </section>

            {/* Documents — internal reference only, never on PDFs */}
            <section className="ui-surface p-6 mb-6" data-testid="view-documents-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-1" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Documents
              </h2>
              <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Internal reference only — documents will not appear on any PDF.</p>
              {documents.length > 0 && (
                <div className="space-y-2 mb-4">
                  {documents.map(d => (
                    <div key={d.id} className="flex items-center gap-3 py-2.5 px-4 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid={`doc-row-${d.id}`}>
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                      <a href={getMediaUrl(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-sm font-medium flex-1 truncate" style={{ color: "var(--primary)" }}>{d.original_filename}</a>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{d.size ? `${(d.size / 1024).toFixed(1)} KB` : ""}</span>
                      <button onClick={async () => { try { await deleteMedia(id, d.id); toast.success("Deleted"); loadQuote(); } catch { toast.error("Failed"); } }} style={{ color: "var(--text-muted)" }}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <label
                className={`flex items-center justify-center gap-2 py-4 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                style={{ border: "2px dashed var(--border)" }}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={async (e) => {
                  e.preventDefault();
                  const files = Array.from(e.dataTransfer.files || []).slice(0, 20);
                  if (files.length === 0) return;
                  setUploading(true);
                  for (const f of files) { try { await uploadMedia(id, f, "document"); } catch (err) { console.error(err); } }
                  toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
                  loadQuote(); setUploading(false);
                }}
              >
                <Upload className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>{uploading ? "Uploading..." : "Upload document or drag & drop"}</span>
                <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple className="hidden" onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, 20);
                  if (files.length === 0) return;
                  setUploading(true);
                  for (const f of files) { try { await uploadMedia(id, f, "document"); } catch (err) { console.error(err); } }
                  toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
                  loadQuote(); setUploading(false); e.target.value = "";
                }} data-testid="view-document-upload" />
              </label>
            </section>

            {/* Timeline */}
            <section className="ui-surface p-6 mb-6" data-testid="view-timeline">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <Clock className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Timeline
              </h2>
              <div>
                <TimelineItem label="Job Created" timestamp={quote.created_at} />
                {(quote.activity_log || []).map((entry, i) => <TimelineItem key={i} label={entry.action} timestamp={entry.timestamp} user={entry.user} />)}
                <TimelineItem label={`Current Status: ${quote.status || "Not Started"}`} timestamp={quote.updated_at} isLast />
              </div>
            </section>

            {/* Internal Notes — always visible, debounced auto-save, syncs with Dashboard notes column */}
            <InternalNotesCard
              quoteId={id}
              initialValue={quote.job_notes || ""}
              isMobile={isMobile}
              onSaved={(val) => setQuote((prev) => (prev ? { ...prev, job_notes: val } : prev))}
            />

            {/* Communication Log */}
            {(quote.activity_log || []).some(e => e.action && (e.action.includes("email") || e.action.includes("Email") || e.action.includes("SMS") || e.action.includes("sent") || e.action.includes("chase"))) && (
              <section className="ui-surface p-6 mb-6">
                <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                  <Send className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Communication Log
                </h2>
                <div className="space-y-2">
                  {(quote.activity_log || []).filter(e => e.action && (e.action.includes("email") || e.action.includes("Email") || e.action.includes("SMS") || e.action.includes("sent") || e.action.includes("chase") || e.action.includes("Booking email"))).map((e, i) => (
                    <div key={i} className="flex items-start gap-3 py-2" style={{ borderBottom: i < (quote.activity_log || []).filter(e2 => e2.action && (e2.action.includes("email") || e2.action.includes("Email") || e2.action.includes("SMS") || e2.action.includes("sent"))).length - 1 ? "1px solid var(--border)" : "none" }}>
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: e.action.includes("SMS") ? "#7C3AED" : "var(--primary)" }} />
                      <div>
                        <p className="text-sm" style={{ color: "var(--text-primary)" }}>{e.action}</p>
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{e.timestamp ? new Date(e.timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""} {e.user && e.user !== "System" ? `· ${e.user}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar — Financial Summary */}
          <div className={isMobile ? "" : "lg:col-span-4"}>
            <div className={isMobile ? "" : "sticky top-6"}>
              <div className="ui-surface overflow-hidden" data-testid="live-total-widget">
                <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border)" }}>
                  <h3 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>Financial Summary</h3>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <SummaryRow label="Net amount" value={totals.totalExVat} bold />
                  <div className="pt-2">
                    <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>Total costs</p>
                    <SummaryRow label="Material costs" value={totals.materialsSubtotal} muted />
                    <SummaryRow label="Labour costs" value={totals.labourTotal} muted />
                    {quote.parking_hours > 0 && quote.parking_rate > 0 && <SummaryRow label="Parking (on quote)" value={totals.parkingExVat} muted />}
                  </div>
                  <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                    <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>Totals</p>
                    <SummaryRow label="Materials (adjusted)" value={totals.materialsTotal} muted />
                    <SummaryRow label="Subtotal Ex VAT" value={totals.totalExVat} />
                    {quote.vat_enabled !== false && <SummaryRow label={`VAT (${FIXED_VAT_RATE}%)`} value={totals.vatAmount} muted />}
                  </div>
                  <div className="pt-3" style={{ borderTop: "2px solid var(--primary)" }}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Grand Total</span>
                      <span className="text-2xl font-mono font-bold" style={{ color: "var(--primary)" }} data-testid="grand-total">{formatCurrency(totals.totalIncVat)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Labour summary */}
              <div className="ui-surface p-5 mt-4">
                <h4 className="text-xs font-semibold tracking-wide uppercase mb-3" style={{ color: "var(--text-muted)" }}>Labour</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Duration</span><span className="font-mono font-semibold">{fmtDuration(quote.labour_hours)}{quote.labour_hours >= HOURS_PER_DAY ? ` (${quote.labour_hours}h)` : ""}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Rate</span><span className="font-mono">{formatCurrency(quote.locked_hourly_rate || getPricing(quote.client_type).hourly_rate || 54)}/h</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Labour Total</span><span className="font-mono font-bold" style={{ color: "var(--primary)" }}>{formatCurrency(totals.labourTotal)}</span></div>
                </div>
              </div>

              {/* Quick info */}
              <div className="ui-surface p-5 mt-4">
                <h4 className="text-xs font-semibold tracking-wide uppercase mb-3" style={{ color: "var(--text-muted)" }}>Quote Info</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Client</span><span className="font-medium">{quote.client_type}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Services</span><span className="font-mono">{actions.filter(a => typeof a === "object" && !a.is_sub).length}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Items</span><span className="font-mono">{items.length}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Photos</span><span className="font-mono">{photos.length}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>VAT</span><span className="font-mono">{quote.vat_enabled !== false ? "Enabled" : "Disabled"}</span></div>
                </div>
              </div>

              {/* Payment Status */}
              {(quote.status === "Invoiced" || quote.invoice_paid || quote.refund_status) && (
                <div className="ui-surface p-5 mt-4" style={{ border: quote.refund_status === "full" ? "2px solid #DC2626" : quote.refund_status === "partial" ? "2px solid #D97706" : quote.invoice_paid ? "2px solid #22C55E" : "2px solid #F59E0B" }}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold tracking-wide uppercase" style={{ color: quote.refund_status ? "#DC2626" : quote.invoice_paid ? "#15803D" : "#92400E" }}>Payment Status</h4>
                    {quote.invoice_paid && (
                      <button onClick={() => { setPaymentMode("edit"); setShowEditPayment(true); }} className="p-1 rounded hover:bg-slate-100" style={{ color: "var(--text-muted)" }} title="Edit payment" data-testid="edit-payment-btn"><Edit className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Invoice</span><span className="font-mono font-bold">{quote.invoice_number || (quote.quote_number || "").replace("Q-", "I-")}</span></div>
                    <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Amount</span><span className="font-mono">{formatCurrency(quote.total_inc_vat)}</span></div>
                    <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Status</span>
                      {quote.refund_status === "full" ? <span className="font-semibold" style={{ color: "#DC2626" }}>Refunded</span>
                       : quote.refund_status === "partial" ? <span className="font-semibold" style={{ color: "#D97706" }}>Partially Refunded</span>
                       : quote.invoice_paid ? <span className="font-semibold" style={{ color: "#15803D" }}>Paid</span>
                       : <span className="font-semibold" style={{ color: "#F59E0B" }}>Unpaid</span>}
                    </div>
                    {quote.invoice_paid && (
                      <>
                        <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Paid</span><span className="font-mono font-bold" style={{ color: "#15803D" }}>{formatCurrency(quote.invoice_paid_amount || quote.total_inc_vat)}</span></div>
                        {quote.invoice_paid_date && <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Paid Date</span><span className="text-xs">{new Date(quote.invoice_paid_date).toLocaleDateString("en-GB")}</span></div>}
                        {quote.payment_method && <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Method</span><span className="text-xs">{quote.payment_method}</span></div>}
                      </>
                    )}
                    {/* Refund summary */}
                    {(quote.total_refunded > 0 || (quote.refunds || []).length > 0) && (() => {
                      const totalRefunded = quote.total_refunded || (quote.refunds || []).reduce((s, r) => s + (r.amount || 0), 0);
                      const netPaid = (quote.invoice_paid_amount || quote.total_inc_vat) - totalRefunded;
                      return (
                        <>
                          <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Refunded</span><span className="font-mono font-bold" style={{ color: "#DC2626" }}>-{formatCurrency(totalRefunded)}</span></div>
                          <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Net Paid</span><span className="font-mono font-bold">{formatCurrency(netPaid)}</span></div>
                        </>
                      );
                    })()}
                    {/* Refund History */}
                    {(quote.refunds || []).length > 0 && (
                      <div className="pt-2 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
                        <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: "var(--text-muted)" }}>Refund History</p>
                        {(quote.refunds || []).map((r, ri) => (
                          <div key={r.id || `r-${ri}`} className="text-xs py-1.5" style={{ borderBottom: ri < (quote.refunds || []).length - 1 ? "1px solid var(--border)" : "none" }}>
                            <div className="flex justify-between"><span className="font-mono font-bold" style={{ color: "#DC2626" }}>-{formatCurrency(r.amount)}</span><span style={{ color: "var(--text-muted)" }}>{r.date ? new Date(r.date).toLocaleDateString("en-GB") : ""}</span></div>
                            <p style={{ color: "var(--text-secondary)" }}>{r.reason}{r.method ? ` · ${r.method}` : ""}</p>
                            {r.notes && <p style={{ color: "var(--text-muted)" }}>{r.notes}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Actions */}
                    {!quote.invoice_paid && !quote.refund_status && (() => {
                      const sentDate = quote.first_sent_date || quote.updated_at;
                      const daysOut = sentDate ? Math.floor((Date.now() - new Date(sentDate).getTime()) / 86400000) : 0;
                      const terms = quote.client_type === "Private" ? 14 : 30;
                      const pct = Math.min(100, Math.round((daysOut / terms) * 100));
                      return (
                        <div className="pt-2">
                          <div className="flex justify-between text-xs mb-1" title={`First sent: ${quote.first_sent_date ? new Date(quote.first_sent_date).toLocaleString() : "—"}\nLast sent: ${quote.last_sent_date ? new Date(quote.last_sent_date).toLocaleString() : "—"}`}>
                            <span style={{ color: "var(--text-muted)" }}>{daysOut} days outstanding <span className="text-[10px]" style={{ color: "var(--text-muted)", opacity: 0.7 }}>since first invoice sent</span></span>
                            <span style={{ color: "var(--text-muted)" }}>{terms} day terms</span>
                          </div>
                          <div className="w-full h-2 rounded-full" style={{ background: "#FEF3C7" }}><div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: pct >= 100 ? "#EF4444" : "#F59E0B" }} /></div>
                        </div>
                      );
                    })()}
                    {!quote.invoice_paid && !quote.refund_status && (
                      <button onClick={() => { setPaymentMode("create"); setShowEditPayment(true); }} className="w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors" style={{ background: "var(--success)", color: "#fff" }} data-testid="qv-mark-paid-btn">Mark as Paid</button>
                    )}
                    {!quote.invoice_paid && !quote.refund_status && (
                      <button onClick={() => setShowSendReminder(true)} className="w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}>Send Reminder</button>
                    )}
                    {/* Chase pause toggle — only shown on unpaid invoices */}
                    {!quote.invoice_paid && !quote.refund_status && (
                      <button
                        onClick={async () => {
                          const next = !quote.chase_paused;
                          // Optimistic update — flip the visual immediately, revert on error.
                          const prev = quote.chase_paused;
                          setQuote(p => ({
                            ...p,
                            chase_paused: next,
                            activity_log: [
                              ...(p.activity_log || []),
                              { action: next ? "Chase emails PAUSED for this invoice" : "Chase emails RESUMED for this invoice", timestamp: new Date().toISOString(), user: localStorage.getItem("desktop_user_name") || "System" },
                            ],
                          }));
                          try {
                            await api.put(`/quotes/${id}/chase-paused`, { paused: next, changed_by: localStorage.getItem("desktop_user_name") || "" });
                            toast.success(next ? "Automated chases paused for this invoice" : "Automated chases resumed");
                          } catch {
                            setQuote(p => ({ ...p, chase_paused: prev, activity_log: (p.activity_log || []).slice(0, -1) }));
                            toast.error("Failed to update chase setting");
                          }
                        }}
                        className="w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                        style={{
                          background: quote.chase_paused ? "#FEF3C7" : "transparent",
                          color: quote.chase_paused ? "#92400E" : "var(--text-muted)",
                          border: quote.chase_paused ? "1px solid #F59E0B" : "1px solid var(--border)",
                        }}
                        data-testid="qv-chase-pause-toggle"
                        title={quote.chase_paused ? "Resume automated chase emails" : "Pause automated chase emails for this invoice"}
                      >
                        {quote.chase_paused ? "▶ Resume Automated Chases" : "⏸ Pause Automated Chases"}
                      </button>
                    )}
                    {quote.invoice_paid && !quote.refund_status && (
                      <button onClick={() => { setRefundData({ refund_amount: "", refund_date: new Date().toISOString().split("T")[0], reason: "", method: "Bank Transfer", notes: "" }); setShowRefund(true); }} className="w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#FEF2F2", color: "#DC2626", border: "1px solid #FCA5A5" }}>Issue Refund</button>
                    )}
                    {quote.refund_status === "partial" && (
                      <button onClick={() => { const remaining = (quote.invoice_paid_amount || quote.total_inc_vat) - (quote.total_refunded || 0); setRefundData({ refund_amount: "", refund_date: new Date().toISOString().split("T")[0], reason: "", method: "Bank Transfer", notes: "" }); setShowRefund(true); }} className="w-full mt-2 py-2 rounded-lg text-xs font-semibold transition-colors" style={{ background: "#FEF2F2", color: "#DC2626", border: "1px solid #FCA5A5" }}>Issue Additional Refund</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legacy delete dialog — kept inert (no trigger). Quote deletion now happens via the
          QuoteFamilySelector per-card overflow menu with the 3-branch safety logic. */}
      <Dialog open={showDelete} onOpenChange={() => { /* no-op */ }}>
        <DialogContent data-testid="view-delete-dialog">
          <DialogHeader><DialogTitle>Delete Quote</DialogTitle><DialogDescription>This action cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline">Cancel</Button><Button variant="destructive" onClick={handleDelete} data-testid="view-confirm-delete-btn">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enlarged photo with navigation */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="p-2" data-testid="enlarged-photo-dialog">
          {enlargedPhoto && (() => {
            const allPhotos = (quote.media || []).filter(m => m.category === "photo" && !m.is_deleted);
            const idx = allPhotos.findIndex(p => p.id === enlargedPhoto.id);
            const hasPrev = idx > 0;
            const hasNext = idx < allPhotos.length - 1;
            return (
              <div className="w-full relative">
                <img src={getMediaUrl(enlargedPhoto.storage_path)} alt={enlargedPhoto.original_filename} className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
                {hasPrev && (
                  <button onClick={() => setEnlargedPhoto(allPhotos[idx - 1])} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <ChevronRight className="w-6 h-6 text-white rotate-180" />
                  </button>
                )}
                {hasNext && (
                  <button onClick={() => setEnlargedPhoto(allPhotos[idx + 1])} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                )}
                <p className="text-xs text-center mt-2" style={{ color: "var(--text-muted)" }}>{enlargedPhoto.original_filename} ({idx + 1}/{allPhotos.length})</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Photo Selection Dialog */}
      {showPhotoSelect && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={!!showPhotoSelect} onOpenChange={() => setShowPhotoSelect(null)}>
        <DialogContent data-testid="photo-select-dialog" style={{ width: "680px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <DialogHeader><DialogTitle>Select Photos</DialogTitle></DialogHeader>
          {(() => {
            const availablePhotos = (quote?.media || []).filter(m => m.category === "photo" && !m.is_deleted);
            const allSelected = availablePhotos.length > 0 && availablePhotos.every(p => selectedPhotoIds.has(p.id));
            return (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>{selectedPhotoIds.size === availablePhotos.length ? `All ${selectedPhotoIds.size}` : selectedPhotoIds.size} selected</p>
                  <button onClick={() => {
                    if (allSelected) { setSelectedPhotoIds(new Set()); }
                    else { setSelectedPhotoIds(new Set(availablePhotos.map(p => p.id))); }
                  }} className="text-xs font-semibold" style={{ color: "var(--primary)" }}>
                    {allSelected ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="grid grid-cols-4 py-2 flex-1 overflow-y-auto" style={{ gap: "8px" }}>
                  {availablePhotos.map(p => {
                    const isSelected = selectedPhotoIds.has(p.id);
                    return (
                      <div key={p.id} className="flex flex-col items-center">
                        <button onClick={() => {
                          setSelectedPhotoIds(prev => {
                            const next = new Set(prev);
                            if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                            return next;
                          });
                        }} className={`rounded-lg overflow-hidden relative transition-all ${isSelected ? "ring-2 ring-blue-500" : "opacity-50 hover:opacity-75"}`} style={{ width: "120px", height: "90px", border: isSelected ? "2px solid var(--primary)" : "2px solid transparent" }}>
                          <LazyPhoto src={getMediaUrl(p.storage_path)} />
                          {isSelected && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center"><svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>}
                        </button>
                        {isSelected && p.uploaded_at && <span className="text-[10px] mt-1" style={{ color: "#6B7280" }}>{new Date(p.uploaded_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoSelect(null)}>Cancel</Button>
            <Button onClick={() => {
              const type = showPhotoSelect;
              setShowPhotoSelect(null);
              if (type === "invoice") setShowSendInvoice(true);
              else setShowSendQuote(true);
            }} className="btn-primary">Continue with {selectedPhotoIds.size} photo{selectedPhotoIds.size !== 1 ? "s" : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Quote */}
      {showSendQuote && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} onClick={() => setShowSendQuote(false)} />}
      <Dialog open={showSendQuote} onOpenChange={setShowSendQuote}>
        <DialogContent data-testid="send-quote-dialog" style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <DialogHeader><DialogTitle>Send Quote</DialogTitle></DialogHeader>
          {quote && showSendQuote && (
            <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); const d = { to_email: fd.get("to_email"), cc_email: fd.get("cc_email"), subject: fd.get("subject"), message: fd.get("message") }; if (!d.to_email) { toast.error("Enter an email"); return; } setSendingQuote(true); sendQuoteEmail(id, d).then(() => { toast.success("Quote sent"); setShowSendQuote(false); navigate("/"); }).catch(() => toast.error("Failed")).finally(() => setSendingQuote(false)); }}>
              <div className="space-y-3 py-2">
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>To Email *</Label><input name="to_email" defaultValue={quote.correspondence_email || ""} placeholder="client@example.com" className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} data-testid="quote-to-email" /></div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>CC (default: contact@hammondpropertysolutions.co.uk)</Label><input name="cc_email" defaultValue="" placeholder="e.g. manager@company.co.uk" className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} /></div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Subject</Label><input name="subject" defaultValue={`${quote.site_address || ""} Quotation is available!`} className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} /></div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Message</Label><textarea name="message" defaultValue={`Hi ${[quote.client_first_name, quote.client_last_name].filter(Boolean).join(" ") || quote.client_type || ""},\n\nPlease see your quotation attached.\n\nIf you have any questions feel free to give us a call.\n\nMany Thanks`} className="w-full rounded-lg p-3 text-sm mt-1 focus:outline-none" style={{ border: "1px solid var(--border)", minHeight: "160px", resize: "vertical", background: "#fff" }} /></div>
              </div>
              <DialogFooter className="flex gap-2">
                <a href={`${getQuotePdfUrl(id)}${selectedPhotoIds.size > 0 ? `?photos=${Array.from(selectedPhotoIds).join(",")}` : ""}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg gap-1 text-sm font-medium h-9 px-3" style={{ border: "1px solid var(--border)" }}><Download className="w-4 h-4" /> PDF</a>
                <Button type="submit" disabled={sendingQuote} className="rounded-lg gap-1" style={{ background: "var(--primary)", color: "#fff" }} data-testid="quote-send-btn"><Send className="w-4 h-4" /> {sendingQuote ? "Sending..." : "Send"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invoice */}
      {showSendInvoice && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} onClick={() => setShowSendInvoice(false)} />}
      <Dialog open={showSendInvoice} onOpenChange={setShowSendInvoice}>
        <DialogContent data-testid="send-invoice-dialog" style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <DialogHeader><DialogTitle>Send Invoice</DialogTitle><DialogDescription>Send invoice for {quote?.invoice_number || (quote?.quote_number || "").replace("Q-", "I-")}.</DialogDescription></DialogHeader>
          {quote && showSendInvoice && (
            <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); const d = { to_email: fd.get("to_email"), cc_email: fd.get("cc_email"), subject: fd.get("subject"), message: fd.get("message") }; if (!d.to_email) { toast.error("Enter an email"); return; } setSendingInvoice(true); sendInvoiceEmail(id, d).then(() => { toast.success("Invoice sent"); setShowSendInvoice(false); navigate("/"); }).catch(() => toast.error("Failed")).finally(() => setSendingInvoice(false)); }}>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>To Email *</Label>
                  <input name="to_email" defaultValue={quote.correspondence_email || ""} placeholder="client@example.com" className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} data-testid="invoice-to-email" />
                  <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Status will update to Invoiced when sent.</p>
                </div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>CC (default: contact@hammondpropertysolutions.co.uk)</Label><input name="cc_email" defaultValue="" placeholder="e.g. manager@company.co.uk" className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} data-testid="invoice-cc-email" /></div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Subject</Label><input name="subject" defaultValue={`Invoice ${(quote.quote_number || "").replace("Q-", "I-")} - ${quote.site_address || ""}`} className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} data-testid="invoice-subject" /></div>
                <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Message</Label><textarea name="message" defaultValue={`Hi ${[quote.client_first_name, quote.client_last_name].filter(Boolean).join(" ") || quote.client_type || ""},\n\nPlease see your invoice attached. Photos are included at the bottom of the document.\n\nIf you have any questions please feel free to reach out.\n\nMany Thanks`} className="w-full rounded-lg p-3 text-sm mt-1 focus:outline-none" style={{ border: "1px solid var(--border)", minHeight: "160px", resize: "vertical", background: "#fff" }} data-testid="invoice-message" /></div>
              </div>
              <DialogFooter className="flex gap-2">
                <a href={`${getInvoicePdfUrl(id)}${selectedPhotoIds.size > 0 ? `?photos=${Array.from(selectedPhotoIds).join(",")}` : ""}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg gap-1 text-sm font-medium h-9 px-3" style={{ border: "1px solid var(--border)" }} data-testid="invoice-download-pdf"><Download className="w-4 h-4" /> PDF</a>
                <Button type="submit" disabled={sendingInvoice} className="rounded-lg gap-1" style={{ background: "var(--primary)", color: "#fff" }} data-testid="invoice-send-btn"><Send className="w-4 h-4" /> {sendingInvoice ? "Sending..." : "Send"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Reminder */}
      {showSendReminder && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} onClick={() => setShowSendReminder(false)} />}
      <Dialog open={showSendReminder} onOpenChange={setShowSendReminder}>
        <DialogContent data-testid="send-reminder-dialog" style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <DialogHeader><DialogTitle>Send Payment Reminder</DialogTitle></DialogHeader>
          {quote && showSendReminder && (() => {
            const invoiceNum = quote.invoice_number || (quote.quote_number || "").replace("Q-", "I-");
            const sentDate = quote.first_sent_date || quote.updated_at;
            const daysOut = sentDate ? Math.floor((Date.now() - new Date(sentDate).getTime()) / 86400000) : 0;
            const clientName = [quote.client_first_name, quote.client_last_name].filter(Boolean).join(" ") || quote.client_type || "";
            const invoiceDate = quote.updated_at ? new Date(quote.updated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—";
            const terms = quote.client_type === "Private" ? 14 : 30;
            const dueDate = quote.updated_at ? new Date(new Date(quote.updated_at).getTime() + terms * 86400000).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—";
            return (
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const d = { to_email: fd.get("to_email"), cc_email: fd.get("cc_email"), subject: fd.get("subject"), message: fd.get("message") };
                if (!d.to_email) { toast.error("Enter an email"); return; }
                setSendingReminder(true);
                try {
                  await sendInvoiceEmail(id, { ...d, is_reminder: true });
                  toast.success("Reminder sent");
                  setShowSendReminder(false);
                  loadQuote();
                } catch { toast.error("Failed to send"); }
                finally { setSendingReminder(false); }
              }}>
                <p className="text-xs font-semibold mb-3 px-2 py-1.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>Days outstanding: {daysOut}</p>
                <div className="space-y-3 py-2">
                  <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>To Email *</Label><input name="to_email" defaultValue={quote.correspondence_email || ""} className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} /></div>
                  <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>CC</Label><input name="cc_email" defaultValue="contact@hammondpropertysolutions.co.uk" className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} /></div>
                  <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Subject</Label><input name="subject" defaultValue={`Payment Reminder — Invoice ${invoiceNum} — ${quote.site_address || ""}`} className="w-full h-10 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)", background: "#fff" }} /></div>
                  <div><Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Message</Label><textarea name="message" defaultValue={`Hi ${clientName},\n\nI hope you're well. I'm writing to follow up on invoice ${invoiceNum} dated ${invoiceDate} for works carried out at ${quote.site_address || ""}.\n\nThe invoice total of ${formatCurrency(quote.total_inc_vat)} was due for payment on ${dueDate} and remains outstanding.\n\nCould you please arrange payment at your earliest convenience? Our bank details are below for your reference:\n\nAccount name: Hammond Property Solutions\nSort code: 04-00-75\nAccount number: 42599614\nReference: ${invoiceNum}\n\nIf payment has already been made, please disregard this message and accept our thanks.\n\nIf you have any queries regarding this invoice please don't hesitate to get in touch.\n\nKind regards,\nHammond Property Solutions\n0203 488 5955`} className="w-full rounded-lg p-3 text-sm mt-1 focus:outline-none" style={{ border: "1px solid var(--border)", minHeight: "200px", resize: "vertical", background: "#fff" }} /></div>
                </div>
                <DialogFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSendReminder(false)}>Cancel</Button>
                  <Button type="submit" disabled={sendingReminder} className="rounded-lg gap-1" style={{ background: "#E05C2A", color: "#fff" }}><Send className="w-4 h-4" /> {sendingReminder ? "Sending..." : "Send Reminder"}</Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Payment Receipt Modal — handles both Mark Paid (create) and Edit Payment */}
      <PaymentReceiptModal
        open={showEditPayment}
        onOpenChange={setShowEditPayment}
        quote={quote}
        pricingRules={pricingRules}
        isEdit={paymentMode === "edit"}
        onSaved={() => loadQuote()}
      />

      {/* Issue Refund Modal */}
      {showRefund && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={showRefund} onOpenChange={setShowRefund}>
        <DialogContent style={{ width: "600px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <DialogHeader><DialogTitle>Issue Refund</DialogTitle></DialogHeader>
          {quote && (() => {
            const maxRefund = (quote.invoice_paid_amount || quote.total_inc_vat) - (quote.total_refunded || 0);
            const amountError = refundData.refund_amount && parseFloat(refundData.refund_amount) > maxRefund;
            return (
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>Refund Amount (£) *</Label>
                  <Input type="number" step="0.01" value={refundData.refund_amount} onChange={(e) => setRefundData(p => ({ ...p, refund_amount: e.target.value }))} className="mt-1 rounded-lg font-mono" style={{ border: amountError ? "2px solid #DC2626" : "1px solid var(--border)" }} />
                  <p className="text-[10px] mt-1" style={{ color: amountError ? "#DC2626" : "var(--text-muted)" }}>{amountError ? `Refund cannot exceed £${maxRefund.toFixed(2)}` : `Maximum refund: £${maxRefund.toFixed(2)}`}</p>
                </div>
                <div><Label className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>Refund Date</Label><Input type="date" value={refundData.refund_date} onChange={(e) => setRefundData(p => ({ ...p, refund_date: e.target.value }))} className="mt-1 rounded-lg" style={{ border: "1px solid var(--border)" }} /></div>
                <div><Label className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>Reason for Refund *</Label>
                  <select value={refundData.reason} onChange={(e) => setRefundData(p => ({ ...p, reason: e.target.value }))} className="w-full h-10 mt-1 px-3 rounded-lg text-sm" style={{ border: !refundData.reason ? "1px solid var(--border)" : "1px solid var(--border)" }}>
                    <option value="">Select reason...</option>
                    <option>Work Not Completed</option><option>Work Disputed</option><option>Overcharge Correction</option><option>Goodwill Gesture</option><option>Duplicate Payment</option><option>Client Request</option><option>Other</option>
                  </select>
                </div>
                <div><Label className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>Refund Method</Label>
                  <select value={refundData.method} onChange={(e) => setRefundData(p => ({ ...p, method: e.target.value }))} className="w-full h-10 mt-1 px-3 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }}>
                    <option>Bank Transfer</option><option>Stripe Refund</option><option>Cash</option><option>Cheque</option><option>Other</option>
                  </select>
                </div>
                <div><Label className="text-xs uppercase" style={{ color: "var(--text-muted)" }}>Additional Notes</Label><textarea value={refundData.notes} onChange={(e) => setRefundData(p => ({ ...p, notes: e.target.value }))} placeholder="Provide any additional details about this refund..." className="w-full rounded-lg p-3 text-sm mt-1 resize-y" style={{ border: "1px solid var(--border)", minHeight: "60px" }} /></div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefund(false)}>Cancel</Button>
            <Button disabled={issuingRefund || !refundData.reason || !refundData.refund_amount} onClick={async () => {
              const maxRefund = (quote.invoice_paid_amount || quote.total_inc_vat) - (quote.total_refunded || 0);
              if (parseFloat(refundData.refund_amount) > maxRefund) { toast.error(`Refund cannot exceed £${maxRefund.toFixed(2)}`); return; }
              setIssuingRefund(true);
              try {
                const userName = localStorage.getItem("desktop_user_name") || "User";
                await api.post(`/quotes/${id}/refund?changed_by=${encodeURIComponent(userName)}`, { ...refundData, refund_amount: parseFloat(refundData.refund_amount) });
                toast.success("Refund issued");
                setShowRefund(false);
                loadQuote();
              } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
              finally { setIssuingRefund(false); }
            }} style={{ background: "#DC2626", color: "#fff" }}>{issuingRefund ? "Processing..." : "Confirm Refund"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rebook dialog */}
      <Dialog open={showRebook} onOpenChange={setShowRebook}>
        <DialogContent data-testid="rebook-dialog">
          <DialogHeader><DialogTitle>Re-book Incomplete Actions</DialogTitle><DialogDescription>Select which actions to re-book.</DialogDescription></DialogHeader>
          {quote && (
            <div className="space-y-2 py-2">
              {(() => {
                const ac = quote.action_completion || {};
                const status = ac.status || {};
                const mainActs = actions.filter(a => typeof a === "object" && !a.is_sub);
                return mainActs.map((a, idx) => ({ ...a, num: idx + 1 })).filter(a => !status[a.num]).map(a => (
                  <label key={a.num} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors" style={{ background: rebookSelected[a.num] ? "var(--primary-light)" : "transparent", border: `1px solid ${rebookSelected[a.num] ? "var(--primary)" : "var(--border)"}` }}>
                    <input type="checkbox" checked={!!rebookSelected[a.num]} onChange={() => setRebookSelected(p => ({ ...p, [a.num]: !p[a.num] }))} className="w-4 h-4 rounded accent-blue-600" />
                    <p className="text-sm font-semibold">{a.num}. {a.title || a.description || "Action"}</p>
                  </label>
                ));
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRebook(false)}>Cancel</Button>
            <Button onClick={async () => {
              const selectedNums = Object.entries(rebookSelected).filter(([,v]) => v).map(([k]) => parseInt(k));
              if (selectedNums.length === 0) { toast.error("Select at least one action"); return; }
              // Keep ALL actions — just track which ones are being recalled
              await updateQuote(id, {
                recall_actions: selectedNums,
                action_completion: null,
                pre_recall_status: quote.status,
                status: "Recall",
              });
              setShowRebook(false); toast.success("Recall created"); navigate(`/calendar?book=${id}&skip_auth=true`);
            }} className="btn-primary gap-1">Confirm &amp; Book</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCell({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-medium tracking-wide uppercase" style={{ color: "var(--text-muted)" }}>{label}</p>
      <p className={`font-medium mt-1 text-sm ${mono ? "font-mono" : ""}`} style={{ color: "var(--text-primary)", wordBreak: "break-word", whiteSpace: "normal" }}>{value || "—"}</p>
    </div>
  );
}

function SummaryRow({ label, value, bold, muted }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className={`text-sm ${bold ? "font-semibold" : ""}`} style={{ color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{label}</span>
      <span className={`font-mono text-sm ${bold ? "font-bold" : ""}`} style={{ color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{formatCurrency(value)}</span>
    </div>
  );
}

function TimelineItem({ label, timestamp, user, isLast }) {
  const dateStr = timestamp ? new Date(timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="flex-shrink-0 mt-1.5 rounded-full" style={{ width: "14px", height: "14px", background: isLast ? "#E05C2A" : "#E5E7EB", border: "2px solid #fff", boxShadow: "0 0 0 1px #E5E7EB" }} />
        {!isLast && <div className="w-px flex-1 my-0.5" style={{ background: "#E5E7EB" }} />}
      </div>
      <div className="pb-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{label}</p>
          {user && user !== "System" ? (
            <span className="badge-pill badge-blue text-[10px]">{user}</span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#6B7280" }}>System</span>
          )}
        </div>
        {dateStr && <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{dateStr}</p>}
      </div>
    </div>
  );
}

function ProfitPromptBanner({ quote, navigate, id }) {
  const dismissKey = `profit_prompt_dismissed_${id}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(dismissKey));
  const [profitStatus, setProfitStatus] = useState(null);   // not_started | in_progress | calculated
  useEffect(() => {
    let cancelled = false;
    if (["Invoiced", "Partially Paid", "Paid", "Partially Refunded", "Refunded"].includes(quote.status)) {
      api.get(`/quotes/${id}/profit`).then(r => { if (!cancelled) setProfitStatus(r.data?.status || "not_started"); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [quote.status, id]);
  const isInvoiced = ["Invoiced", "Partially Paid", "Paid", "Partially Refunded", "Refunded"].includes(quote.status);
  if (!isInvoiced || profitStatus === "calculated") return null;
  if (profitStatus === "in_progress") {
    return (
      <div className="mb-4 rounded-xl p-4 flex items-center justify-between gap-3" style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }} data-testid="profit-prompt-in-progress">
        <p className="text-sm" style={{ color: "#92400E" }}><strong>Profit calculation in progress.</strong> Continue where you left off to lock in this job's margin.</p>
        <Button size="sm" onClick={() => navigate(`/quotes/${id}/profit`)} style={{ background: "#E05C2A", color: "#fff" }} data-testid="profit-prompt-continue">Continue</Button>
      </div>
    );
  }
  if (dismissed) return null;
  return (
    <div className="mb-4 rounded-xl p-4 flex items-center justify-between gap-3" style={{ background: "#FFF5F0", border: "1px solid #FED7AA" }} data-testid="profit-prompt-banner">
      <div className="flex items-center gap-3 min-w-0">
        <PoundSterling className="w-5 h-5 flex-shrink-0" style={{ color: "#E05C2A" }} />
        <p className="text-sm" style={{ color: "var(--text-primary)" }}>
          <strong>Invoice sent — ready to calculate job profit?</strong> Complete the cost breakdown to see your margin on this job.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button size="sm" onClick={() => navigate(`/quotes/${id}/profit`)} style={{ background: "#E05C2A", color: "#fff" }} data-testid="profit-prompt-complete">Complete Now</Button>
        <Button size="sm" variant="outline" onClick={() => { localStorage.setItem(dismissKey, "1"); setDismissed(true); }} data-testid="profit-prompt-later">Do Later</Button>
      </div>
    </div>
  );
}


function InternalNotesCard({ quoteId, initialValue, isMobile, onSaved }) {
  const [value, setValue] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const lastSavedRef = useRef(initialValue || "");

  // Sync if parent reloads with new server value (e.g., another tab/user wrote it)
  useEffect(() => {
    if ((initialValue || "") !== lastSavedRef.current && document.activeElement !== textareaRef.current) {
      setValue(initialValue || "");
      lastSavedRef.current = initialValue || "";
    }
  }, [initialValue]);

  // Auto-grow on initial mount + when value changes
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  }, [value]);

  const persist = useCallback(async (next) => {
    if (next === lastSavedRef.current) return;
    setSaving(true);
    try {
      await updateQuote(quoteId, { job_notes: next });
      lastSavedRef.current = next;
      onSaved && onSaved(next);
      setShowSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setShowSaved(false), 1800);
    } catch (e) {
      console.error("Failed to save notes", e);
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  }, [quoteId, onSaved]);

  const pendingValueRef = useRef(initialValue || "");
  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    pendingValueRef.current = next;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(next), 1000);
  };

  // Flush pending save once when the component unmounts (mount-only cleanup, ref keeps latest value)
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        const pending = pendingValueRef.current;
        if (pending !== lastSavedRef.current) {
          updateQuote(quoteId, { job_notes: pending }).catch(() => {});
        }
      }
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minH = isMobile ? 80 : 100;

  return (
    <section className="ui-surface p-6 mb-6" data-testid="internal-notes-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold tracking-wide uppercase flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <NotebookPen className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          Internal Notes
        </h2>
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          Visible to your team only — not included on quotes or invoices.
        </span>
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder="Add internal notes here — visible to your team only. Use this for access instructions, client preferences, site-specific information, or anything the team needs to know about this job."
        className="w-full rounded-lg p-3 text-sm resize-none"
        style={{
          minHeight: `${minH}px`,
          border: "1px solid var(--border)",
          background: "var(--surface)",
          color: "var(--text-primary)",
          overflow: "hidden",
        }}
        data-testid="internal-notes-textarea"
      />
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{value.length} character{value.length === 1 ? "" : "s"}</p>
        <div className="flex items-center gap-3">
          <span className="text-[11px]" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
            Internal use only — not shown on client documents.
          </span>
          <span
            className="text-[11px] inline-flex items-center gap-1 transition-opacity"
            style={{ color: "#16a34a", opacity: showSaved ? 1 : 0, fontWeight: 600 }}
            data-testid="internal-notes-saved-indicator"
            aria-live="polite"
          >
            <Check className="w-3 h-3" />
            {saving ? "Saving…" : "Saved"}
          </span>
        </div>
      </div>
    </section>
  );
}



function MarkdownText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="list-disc pl-5 space-y-1 text-sm mb-2" style={{ color: "var(--text-primary)" }}>{listItems}</ul>);
      listItems = [];
    }
  };

  const renderInline = (str) => {
    // Handle **bold** and *italic*
    const parts = [];
    let remaining = str;
    let key = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const italicMatch = remaining.match(/\*(.+?)\*/);
      const match = boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index) ? boldMatch : italicMatch;
      if (match) {
        if (match.index > 0) parts.push(<span key={key++}>{remaining.slice(0, match.index)}</span>);
        if (match[0].startsWith("**")) parts.push(<strong key={key++}>{match[1]}</strong>);
        else parts.push(<em key={key++}>{match[1]}</em>);
        remaining = remaining.slice(match.index + match[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }
    return parts;
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      listItems.push(<li key={`li-${i}`}>{renderInline(trimmed.slice(2))}</li>);
    } else {
      flushList();
      if (trimmed === "") {
        elements.push(<div key={`br-${i}`} className="h-2" />);
      } else {
        elements.push(<p key={`p-${i}`} className="text-sm mb-1" style={{ color: "var(--text-primary)" }}>{renderInline(trimmed)}</p>);
      }
    }
  });
  flushList();

  return <div>{elements}</div>;
}

function LazyPhoto({ src }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { rootMargin: "100px" });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full h-full" style={{ background: "#F3F4F6" }}>
      {inView && (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 200ms" }}
          onLoad={() => setLoaded(true)}
          loading="lazy"
        />
      )}
    </div>
  );
}
